// Session detail drawer — deep-linkable (training/sessions/:sessionId). Shows
// what/when/where, why the viewer is attending (server-evaluated reason), the
// viewer's own completion state, their track and attendance (Phase 3, with
// Confirm / Check in inside the server's windows), "Need help?" champions,
// and — for learning:manage / learning:assign holders, the session's trainer
// and its champions — the staff area: Roster · Tracks · Attendance · Calendar
// · Practice (Phase 4 validator queue). Phase 4 also adds the participant's
// "Your completion" card (server-evaluated checklist + practical exercise +
// Mark complete, whose 400 lists what is still unmet) and, per roster row, a
// Completion expander (checklist · sign-offs · validate · force complete).
// The backend narrows the roster/attendance to the caller's reach and decides
// who may mark/validate/sign; this sheet only reflects what it returns.
import { useT } from '../lib/i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/features/shared/components/ui/sheet';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { Calendar, CheckCircle2, ChevronDown, ChevronUp, ExternalLink, Link2, Loader2, MapPin, Paperclip, Pencil, Plus, RefreshCw, ShieldCheck, Trash2, User, Users, Video } from 'lucide-react';
import { useHasPermission } from '@/rbac/useRbac';
import { useAppSelector } from '@/app/hooks';
import { getApiErrorMessage } from '@/lib/apiError';
import type { MySession, Session, Track } from '@/services/learningService';
import {
  useAssignmentCompletion, useAttendance, useAudienceFacets, useCompleteAssignment, useCreateTrack, useDeleteTrack, useExcludeParticipant, useIncludeParticipants, useMarkAttendance,
  useParticipants, usePinParticipantTrack, useSelfAttendance, useSession, useSyncSessionCalendar, useTrackSummary, useTracks, useUpdateTrack,
} from '../api/hooks';
import { usePeopleDirectory } from '../lib/usePeopleDirectory';
import { formatSessionRange, sessionZoneNote } from '../lib/format';
import { selfAttendanceAction } from '../lib/attendance';
import { useRuleVocabulary } from '../lib/audienceRules';
import { manageCompletionHref } from '../lib/completion';
import { AssignmentTypeBadge } from './AssignmentTypeBadge';
import { AttendanceChip } from './AttendanceChip';
import { AttendanceGrid } from './AttendanceGrid';
import { MirrorStatusBadge } from './MirrorStatusBadge';
import { ReasonLine } from './ReasonLine';
import { SessionStatusChip } from './SessionStatusChip';
import { TrackEditor } from './TrackEditor';
import { CompletionChecklist } from './CompletionChecklist';
import { PracticePanel } from './PracticePanel';
import { ParticipantCompletion } from './ParticipantCompletion';
import { ValidationQueue } from './ValidationQueue';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';

export type SheetSection = 'roster' | 'tracks' | 'attendance' | 'calendar' | 'practice';

interface Props {
  sessionId: string | null;
  /** Optional already-loaded row (journey/calendar) so the sheet paints instantly. */
  initial?: Session | null;
  onClose: () => void;
  /** Staff section to open on (quick links from the admin / team pages). */
  section?: SheetSection | null;
}

export const deliveryModeLabel = (t: (k: string, d: string) => string, mode?: string): string => ({
  in_person: t('training:mode_in_person', 'In person'),
  virtual: t('training:mode_virtual', 'Virtual'),
  hybrid: t('training:mode_hybrid', 'Hybrid'),
  self_paced: t('training:mode_self_paced', 'Self-paced'),
} as Record<string, string>)[mode || ''] || (mode || '');

const selectCls = 'h-7 rounded-md border border-border bg-background px-1.5 text-xs text-foreground disabled:opacity-50';

export const SessionDetailSheet: React.FC<Props> = ({ sessionId, initial, onClose, section }) => {
  const t = useT();
  const confirm = useConfirm();
  const canManage = useHasPermission('learning:manage');
  const canAssign = useHasPermission('learning:assign');
  const staff = canManage || canAssign;
  const myUserId = useAppSelector((s) => s.auth?.user?.id) as string | undefined;
  const q = useSession(sessionId);
  const session = q.data ?? (initial && initial.id === sessionId ? initial : null);
  // Phase 3 roles the API grants without learning:* permissions.
  const isTrainer = !!myUserId && !!session?.trainer_user_id && session.trainer_user_id === myUserId;
  const isChampion = !!myUserId && (session?.champions || []).some((c) => c.user_id === myUserId);
  const canMark = staff || isTrainer;
  const canViewAttendance = canMark || isChampion;
  const showRoster = staff || isChampion;
  const showTracks = staff || isTrainer || isChampion;
  const showCalendar = canManage;
  // Phase 4: the validator queue is for staff and the trainer (the API narrows/refuses per verification kind).
  const showPractice = staff || isTrainer;
  const tabs = useMemo(() => ([
    ...(showRoster ? (['roster'] as SheetSection[]) : []), ...(showTracks ? (['tracks'] as SheetSection[]) : []),
    ...(canViewAttendance ? (['attendance'] as SheetSection[]) : []), ...(showCalendar ? (['calendar'] as SheetSection[]) : []),
    ...(showPractice ? (['practice'] as SheetSection[]) : []),
  ]), [showRoster, showTracks, canViewAttendance, showCalendar, showPractice]);
  const [tab, setTab] = useState<SheetSection>(section || 'roster');
  useEffect(() => { setTab(section && tabs.includes(section) ? section : tabs[0] || 'roster'); }, [sessionId, section, tabs]);

  const participants = useParticipants(sessionId, showRoster);
  const people = usePeopleDirectory(staff && !!sessionId);
  const tracksQ = useTracks(sessionId, staff);
  const attendanceQ = useAttendance(sessionId, canViewAttendance && tab === 'attendance');
  const summaryQ = useTrackSummary(sessionId, canViewAttendance && tab === 'attendance');
  const facetsQ = useAudienceFacets(staff && tab === 'tracks');
  const vocab = useRuleVocabulary(t, facetsQ.data);
  const include = useIncludeParticipants();
  const exclude = useExcludeParticipant();
  const complete = useCompleteAssignment();
  const selfAtt = useSelfAttendance();
  const pin = usePinParticipantTrack();
  const createTrack = useCreateTrack();
  const updateTrack = useUpdateTrack();
  const deleteTrack = useDeleteTrack();
  const mark = useMarkAttendance();
  const sync = useSyncSessionCalendar();
  const [evidence, setEvidence] = useState('');
  const [completing, setCompleting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [selfError, setSelfError] = useState<string | null>(null);
  const [trackEditor, setTrackEditor] = useState<{ track?: Track | null } | null>(null);
  // Phase 4: the participant's completion view + inline "not satisfied" feedback; per-row roster expanders.
  const [completeError, setCompleteError] = useState<string | null>(null);
  const [expandedCompletion, setExpandedCompletion] = useState<Record<string, boolean>>({});
  useEffect(() => { setSelfError(null); setTrackEditor(null); setAdding(false); setCompleteError(null); setCompleting(false); setExpandedCompletion({}); }, [sessionId]);
  const { pathname } = useLocation();
  const manageHref = canManage ? manageCompletionHref(pathname) : null;

  const me = session?.me;
  const completionQ = useAssignmentCompletion(me?.assignment_id, !!me?.assignment_id);
  const completion = completionQ.data;
  const journeyStatus = (session as (Session & Partial<Pick<MySession, 'journey_status'>>) | null)?.journey_status;
  const now = Date.now();
  const ended = session ? new Date(session.ends_at).getTime() <= now : false;
  const started = session ? new Date(session.starts_at).getTime() <= now : false;
  const canComplete = !!me?.assignment_id && (me?.status === 'assigned' || me?.status === 'in_progress')
    && session?.status !== 'cancelled' && (ended || started || session?.status === 'in_progress');
  const selfAction = session && me ? selfAttendanceAction(session.starts_at, session.ends_at, session.status, me.attendance, now) : null;
  // A draft session has no roster yet (publish resolves the audience) and the
  // API refuses marks on draft/cancelled sessions — say so instead of showing
  // an empty grid with a dead Save.
  const isDraft = session?.status === 'draft';
  const attendanceClosed = isDraft || session?.status === 'cancelled';

  const roster = participants.data || [];
  const active = roster.filter((p) => !p.removed_at);
  const onRoster = useMemo(() => new Set(active.map((p) => p.user_id)), [active]);
  const candidates = useMemo(() => (people.data || [])
    .filter((p) => !onRoster.has(p.user_id))
    .filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase())), [people.data, onRoster, search]);
  // Tracks: admins get the full rows (rule + counts); trainers/champions only what the session carries.
  const tracks: Track[] = useMemo(() => [...((staff ? tracksQ.data : null) ?? session?.tracks ?? [])].sort((a, b) => a.sequence - b.sequence), [staff, tracksQ.data, session?.tracks]);
  const myTrack = me?.track_id ? tracks.find((x) => x.id === me.track_id) : undefined;
  const attendanceByUser = useMemo(() => new Map((attendanceQ.data || []).map((a) => [a.user_id, a])), [attendanceQ.data]);
  const champions = session?.champions || [];

  const joinUrl = session?.meeting_url && /^https?:\/\//i.test(session.meeting_url) ? session.meeting_url : null;
  const tabLabel: Record<SheetSection, string> = {
    roster: t('training:participants', 'Participants'), tracks: t('training:tracks', 'Tracks'),
    attendance: t('training:attendance', 'Attendance'), calendar: t('training:calendar', 'Calendar'),
    practice: t('training:tab_practice_short', 'Practice'),
  };

  return (
    <Sheet open={!!sessionId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        {!session ? (
          <div className="py-10 flex items-center justify-center text-muted-foreground">
            {q.isError ? <span className="text-sm">{t('training:session_not_found', 'This session is not available to you.')}</span> : <Loader2 className="h-5 w-5 animate-spin" aria-label={t('training:loading', 'Loading')} />}
          </div>
        ) : (
          <>
            <SheetHeader>
              <div className="flex flex-wrap items-center gap-1.5">
                <SessionStatusChip status={journeyStatus || session.status} />
                <AssignmentTypeBadge type={me?.assignment_type} />
                {staff && <MirrorStatusBadge status={session.mirror_status} error={session.mirror_error} />}
                {session.program_name && <span className="text-xs text-muted-foreground">· {session.program_name}</span>}
              </div>
              <SheetTitle className="text-left">{session.title}</SheetTitle>
              <SheetDescription className="text-left">
                {formatSessionRange(session.starts_at, session.ends_at, undefined, session.timezone)}
                {sessionZoneNote(session.timezone) ? <span className="block text-[11px]">{t('training:session_tz', 'Session time zone')}: {sessionZoneNote(session.timezone)}</span> : null}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4 text-sm">
              <div className="grid gap-2">
                <div className="flex items-center gap-2 text-foreground"><Video className="w-4 h-4 text-muted-foreground" aria-hidden /> {deliveryModeLabel(t, session.delivery_mode)}</div>
                {session.location && <div className="flex items-center gap-2 text-foreground"><MapPin className="w-4 h-4 text-muted-foreground" aria-hidden /> {session.location}</div>}
                {joinUrl && (
                  <a className="inline-flex items-center gap-2 text-orange-600 hover:underline" href={joinUrl} target="_blank" rel="noreferrer">
                    <Link2 className="w-4 h-4" aria-hidden /> {t('training:join_meeting', 'Join meeting')} <ExternalLink className="w-3 h-3" aria-hidden />
                  </a>
                )}
                {!joinUrl && session.generate_meeting_link && session.status === 'draft' && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><Link2 className="w-3.5 h-3.5" aria-hidden /> {t('training:mint_link_short', 'meeting link minted on publish')}</div>
                )}
                {session.trainer_label && <div className="flex items-center gap-2 text-foreground"><User className="w-4 h-4 text-muted-foreground" aria-hidden /> {t('training:trainer', 'Trainer')}: {session.trainer_label}</div>}
                {typeof session.capacity === 'number' && session.capacity > 0 && (
                  <div className="flex items-center gap-2 text-muted-foreground"><Users className="w-4 h-4" aria-hidden /> {t('training:capacity_of', '{{n}} of {{cap}} seats', { n: session.participant_count, cap: session.capacity })}</div>
                )}
              </div>

              {session.objectives && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('training:objectives', 'Objectives')}</h3>
                  <p className="whitespace-pre-line text-foreground">{session.objectives}</p>
                </section>
              )}
              {session.description && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('training:description', 'Description')}</h3>
                  <p className="whitespace-pre-line text-muted-foreground">{session.description}</p>
                </section>
              )}
              {(session.materials || []).length > 0 && (
                <section>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{t('training:materials', 'Materials')}</h3>
                  <ul className="space-y-1">
                    {session.materials.map((m, i) => (
                      <li key={`${m.url}-${i}`}>
                        <a className="inline-flex items-center gap-1.5 text-orange-600 hover:underline" href={m.url} target="_blank" rel="noreferrer">
                          <Paperclip className="w-3.5 h-3.5" aria-hidden /> {m.title || m.url}
                        </a>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {me && me.track_name && (
                <section className="rounded-md border border-border p-3 space-y-1" data-testid="my-track">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:your_track', 'Your track')}</h3>
                  <div className="font-medium text-foreground">{me.track_name}</div>
                  {myTrack?.description && <p className="text-xs text-muted-foreground">{myTrack.description}</p>}
                  {myTrack?.objectives && (
                    <div><div className="text-[11px] font-medium text-muted-foreground">{t('training:objectives', 'Objectives')}</div><p className="whitespace-pre-line text-foreground">{myTrack.objectives}</p></div>
                  )}
                  {myTrack?.instructions && (
                    <div><div className="text-[11px] font-medium text-muted-foreground">{t('training:track_instructions', 'Instructions for participants')}</div><p className="whitespace-pre-line text-foreground">{myTrack.instructions}</p></div>
                  )}
                </section>
              )}

              {me && (
                <section className="rounded-md border border-border p-3 space-y-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:why_attending', "Why I'm attending")}</h3>
                  <div className="flex items-center gap-2"><AssignmentTypeBadge type={me.assignment_type} /></div>
                  <ReasonLine type={me.assignment_type} reason={me.reason} />
                  <div className="flex items-center gap-2 text-xs">
                    {me.completed_at
                      ? <span className="inline-flex items-center gap-1 text-success-strong"><CheckCircle2 className="w-4 h-4" aria-hidden /> {t('training:completed_on', 'Completed {{when}}', { when: new Date(me.completed_at).toLocaleString() })}</span>
                      : <span className="text-muted-foreground">{t('training:my_status', 'My status')}: {me.status.replace('_', ' ')}</span>}
                  </div>
                  <div className="flex flex-wrap items-center gap-2" data-testid="my-attendance">
                    <span className="text-xs text-muted-foreground">{t('training:attendance', 'Attendance')}:</span>
                    <AttendanceChip status={me.attendance} />
                    {selfAction && (
                      <Button size="sm" disabled={selfAtt.isPending} data-testid="self-attendance" onClick={() => { setSelfError(null); selfAtt.mutate({ sessionId: session.id, action: selfAction }, {
                        onSuccess: () => toast.success(selfAction === 'confirm' ? t('training:att_confirmed_ok', 'Attendance confirmed') : t('training:att_checked_in_ok', 'Checked in')),
                        onError: (e) => setSelfError(getApiErrorMessage(e, t('training:err', 'Something went wrong'))),
                      }); }}>
                        {selfAtt.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : selfAction === 'confirm' ? t('training:att_confirm', 'Confirm attendance') : t('training:att_checkin', 'Check in')}
                      </Button>
                    )}
                  </div>
                  {selfError && <div role="alert" className="text-xs text-red-600 dark:text-red-400">{selfError}</div>}
                </section>
              )}

              {me && me.assignment_id && (
                <section className="rounded-md border border-border p-3 space-y-2" data-testid="my-completion">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:your_completion', 'Your completion')}</h3>
                  {completionQ.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
                  {completion && <CompletionChecklist view={completion} canReadCatalog={staff} />}
                  {completion?.practice && <PracticePanel view={completion} canSubmit={completion.status !== 'completed' && completion.status !== 'verified'} canReadCatalog={staff} idPrefix="sd-pp" className="border-t border-border pt-2" />}
                  {canComplete && !completing && (
                    <Button size="sm" variant="outline" onClick={() => { setCompleting(true); setCompleteError(null); }} data-testid="mark-complete">{t('training:mark_complete', 'Mark complete')}</Button>
                  )}
                  {completing && (
                    <div className="space-y-2" data-testid="complete-form">
                      <div className="space-y-1">
                        <Label htmlFor="ld-evidence" className="text-xs">{t('training:evidence', 'Evidence link (optional)')}</Label>
                        <Input id="ld-evidence" className="h-8" value={evidence} onChange={(e) => setEvidence(e.target.value)} placeholder="https://…" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => { setCompleting(false); setCompleteError(null); }}>{t('training:cancel', 'Cancel')}</Button>
                        <Button size="sm" disabled={complete.isPending} data-testid="confirm-complete" onClick={() => { setCompleteError(null); complete.mutate(
                          { id: me.assignment_id as string, body: { evidence_url: evidence || undefined }, quiet: true },
                          {
                            onSuccess: () => { toast.success(t('training:completed_ok', 'Marked complete')); setCompleting(false); setEvidence(''); },
                            onError: (e) => setCompleteError(getApiErrorMessage(e, t('training:err', 'Something went wrong'))),
                          },
                        ); }}>{complete.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:confirm_complete', 'Complete')}</Button>
                      </div>
                    </div>
                  )}
                  {completeError && (
                    <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-2 text-xs text-amber-800 dark:text-amber-300 space-y-1" data-testid="complete-unmet">
                      <div>{completeError}</div>
                      {(completion?.unmet || []).length > 0 && (
                        <ul className="list-disc pl-4">{(completion?.unmet || []).map((u) => <li key={u}>{u}</li>)}</ul>
                      )}
                    </div>
                  )}
                </section>
              )}

              {champions.length > 0 && (
                <section className="rounded-md border border-border p-3 space-y-1" data-testid="need-help">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" aria-hidden /> {t('training:need_help', 'Need help?')}</h3>
                  <ul className="space-y-0.5">
                    {champions.map((c) => (
                      <li key={c.id} className="text-foreground">{c.name}{c.label ? <span className="text-xs text-muted-foreground"> · {c.label}</span> : null}</li>
                    ))}
                  </ul>
                </section>
              )}

              {tabs.length > 0 && (
                <section className="space-y-2" data-testid="staff-area">
                  <div role="tablist" aria-label={t('training:staff_sections', 'Session management')} className="flex flex-wrap gap-1 rounded-md bg-muted p-1">
                    {tabs.map((k) => (
                      <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                        className={`rounded-sm px-2.5 py-1 text-xs font-medium transition ${tab === k ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                        {tabLabel[k]}
                      </button>
                    ))}
                  </div>

                  {tab === 'roster' && showRoster && (
                    <div className="space-y-2" role="tabpanel">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:participants', 'Participants')} ({active.length})</h3>
                        {staff && session.status !== 'cancelled' && (
                          <Button size="sm" variant="outline" onClick={() => { setAdding((v) => !v); setPicked([]); setSearch(''); }}>
                            <Plus className="mr-1 h-4 w-4" />{t('training:add_people', 'Add people')}
                          </Button>
                        )}
                      </div>
                      {adding && (
                        <div className="rounded-md border border-orange-300 bg-orange-50 dark:bg-orange-500/10 p-2 space-y-2">
                          <Input aria-label={t('training:search_people', 'Search people')} className="h-8" placeholder={t('training:search_people', 'Search people')} value={search} onChange={(e) => setSearch(e.target.value)} />
                          <div className="max-h-40 overflow-y-auto space-y-1">
                            {people.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
                            {candidates.slice(0, 100).map((p) => (
                              <label key={p.user_id} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" className="accent-orange-500" checked={picked.includes(p.user_id)}
                                  onChange={(e) => setPicked(e.target.checked ? [...picked, p.user_id] : picked.filter((x) => x !== p.user_id))} />
                                {p.name}
                              </label>
                            ))}
                            {!people.isLoading && candidates.length === 0 && <div className="text-xs text-muted-foreground">{t('training:no_people_match', 'No one matches.')}</div>}
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>{t('training:cancel', 'Cancel')}</Button>
                            <Button size="sm" disabled={picked.length === 0 || include.isPending} onClick={() => include.mutate(
                              { sessionId: session.id, body: { user_ids: picked } },
                              { onSuccess: () => {
                                toast.success(isDraft
                                  ? t('training:people_queued', '{{n}} saved — they join the roster when the program is published', { n: picked.length })
                                  : t('training:people_added', '{{n}} added', { n: picked.length }));
                                setAdding(false); setPicked([]);
                              } },
                            )}>{include.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : t('training:add_selected', 'Add {{n}}', { n: picked.length })}</Button>
                          </div>
                        </div>
                      )}
                      {staff && isDraft && (
                        <p className="text-[11px] text-muted-foreground" data-testid="roster-draft-note">
                          {t('training:roster_draft', 'Draft session — people you add are kept with the session and join the roster when the program is published.')}
                        </p>
                      )}
                      {participants.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
                      {active.length === 0 && !participants.isLoading && <p className="text-xs text-muted-foreground">{t('training:no_participants', 'No participants yet.')}</p>}
                      <ul className="divide-y divide-border rounded-md border border-border">
                        {active.map((p) => {
                          const current = p.track_id ?? attendanceByUser.get(p.user_id)?.track_id ?? '';
                          const expanded = !!expandedCompletion[p.id];
                          return (
                            <li key={p.id} className="px-2.5 py-2 space-y-2">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm text-foreground truncate">{p.name}</div>
                                <div className="text-[11px] text-muted-foreground truncate">{p.department || '—'} · {p.status.replace('_', ' ')}{!staff && p.track_name ? ` · ${p.track_name}` : ''}</div>
                              </div>
                              <div className="flex items-center gap-1.5 shrink-0">
                                {(staff || isTrainer) && p.assignment_id && (
                                  <button type="button" aria-expanded={expanded} aria-label={t('training:completion_for', 'Completion for {{name}}', { name: p.name })} className="inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                                    onClick={() => setExpandedCompletion((m) => ({ ...m, [p.id]: !m[p.id] }))} data-testid={`completion-toggle-${p.user_id}`}>
                                    {t('training:completion', 'Completion')} {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                                  </button>
                                )}
                                {staff && tracks.length > 0 && (
                                  <select aria-label={t('training:pin_track_for', 'Track for {{name}}', { name: p.name })} className={selectCls} value={current || ''} disabled={pin.isPending}
                                    onChange={(e) => pin.mutate({ sessionId: session.id, userId: p.user_id, trackId: e.target.value || null }, { onSuccess: () => toast.success(e.target.value ? t('training:track_pinned', 'Pinned to track') : t('training:track_unpinned', 'Track unpinned — the platform decides again')) })}>
                                    <option value="">{t('training:track_auto', 'Auto')}</option>
                                    {tracks.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                                  </select>
                                )}
                                <AssignmentTypeBadge type={p.assignment_type} />
                                {staff && (
                                  <button type="button" aria-label={t('training:remove_person', 'Remove {{name}}', { name: p.name })} className="text-muted-foreground hover:text-red-500 disabled:opacity-50"
                                    disabled={exclude.isPending}
                                    onClick={() => exclude.mutate({ sessionId: session.id, userId: p.user_id }, { onSuccess: () => toast.success(t('training:person_removed', 'Removed')) })}>
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </div>
                            {expanded && p.assignment_id && (
                              <ParticipantCompletion assignmentId={p.assignment_id} learnerName={p.name} canManage={canManage} canManagerSign={staff} canTrainerSign={canManage || isTrainer} canReadCatalog={staff} className="rounded-md bg-muted/40 p-2" />
                            )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}

                  {tab === 'tracks' && showTracks && (
                    <div className="space-y-2" role="tabpanel" data-testid="tracks-panel">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:tracks', 'Tracks')} ({tracks.length})</h3>
                        {canManage && !trackEditor && session.status !== 'cancelled' && (
                          <Button size="sm" variant="outline" onClick={() => setTrackEditor({})}><Plus className="mr-1 h-4 w-4" />{t('training:track_add', 'Add track')}</Button>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">{t('training:track_rule_help', 'A track without a rule is the default for everyone else. The platform assigns each participant to the first track (by order) whose rule matches; pinned people stay where you put them.')}</p>
                      {staff && tracksQ.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
                      {tracks.length === 0 && !tracksQ.isLoading && <p className="text-xs text-muted-foreground">{t('training:wiz_no_tracks', 'No tracks — everyone follows the session as one group.')}</p>}
                      {tracks.length > 0 && (
                        <ol className="divide-y divide-border rounded-md border border-border">
                          {tracks.map((x) => (
                            <li key={x.id} className="px-2.5 py-2 space-y-0.5">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="min-w-0 text-sm text-foreground">{x.sequence}. {x.name} {typeof x.participant_count === 'number' && <span className="text-xs text-muted-foreground">· {t('training:participants_n', '{{n}} participant(s)', { n: x.participant_count })}</span>}</div>
                                {canManage && (
                                  <div className="flex gap-1">
                                    <button type="button" aria-label={t('training:track_edit_named', 'Edit track {{name}}', { name: x.name })} className="text-muted-foreground hover:text-foreground" onClick={() => setTrackEditor({ track: x })}><Pencil className="h-4 w-4" /></button>
                                    <button type="button" aria-label={t('training:track_remove_named', 'Remove track {{name}}', { name: x.name })} className="text-muted-foreground hover:text-red-500 disabled:opacity-50" disabled={deleteTrack.isPending}
                                      onClick={async () => { if (await confirm({ title: t('training:track_delete', 'Delete track'), message: t('training:track_delete_confirm', 'Delete this track? Its participants fall back to the default track.'), danger: true, confirmLabel: t('training:delete', 'Delete') })) deleteTrack.mutate({ trackId: x.id, sessionId: session.id }, { onSuccess: () => toast.success(t('training:track_deleted', 'Track deleted')) }); }}><Trash2 className="h-4 w-4" /></button>
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {'rule' in x ? (x.rule ? vocab.sentence(x.rule) : t('training:track_default', 'Default track (no rule)')) : null}
                                {x.assignment_type ? ` · ${x.assignment_type}` : ''}
                              </div>
                              {x.objectives && <div className="text-xs text-muted-foreground whitespace-pre-line">{x.objectives}</div>}
                            </li>
                          ))}
                        </ol>
                      )}
                      {trackEditor && canManage && (
                        <TrackEditor key={trackEditor.track?.id || 'new'} idPrefix="sd-track" defaultSequence={tracks.length + 1} manageHref={manageHref}
                          initial={trackEditor.track ? { id: trackEditor.track.id, name: trackEditor.track.name, description: trackEditor.track.description, sequence: trackEditor.track.sequence, assignment_type: (trackEditor.track.assignment_type || undefined) as any, objectives: trackEditor.track.objectives, instructions: trackEditor.track.instructions, rule: trackEditor.track.rule ?? undefined, completion_policy_id: trackEditor.track.completion_policy_id ?? undefined, practice_id: trackEditor.track.practice_id ?? undefined } : null}
                          busy={createTrack.isPending || updateTrack.isPending}
                          onCancel={() => setTrackEditor(null)}
                          onSave={(input) => {
                            const done = () => { toast.success(t('training:track_saved', 'Track saved')); setTrackEditor(null); };
                            if (trackEditor.track) updateTrack.mutate({ trackId: trackEditor.track.id, sessionId: session.id, body: input }, { onSuccess: done });
                            else createTrack.mutate({ sessionId: session.id, body: input }, { onSuccess: done });
                          }} />
                      )}
                    </div>
                  )}

                  {tab === 'attendance' && canViewAttendance && (
                    <div className="space-y-2" role="tabpanel" data-testid="attendance-panel">
                      {(summaryQ.data || []).length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="track-summary">
                          {(summaryQ.data || []).map((row) => (
                            <span key={row.track_id || row.track_name || 'all'} className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground">
                              <span className="font-medium text-foreground">{row.track_name || t('training:track_default', 'Default track (no rule)')}</span> · {t('training:summary_counts', '{{attended}}/{{count}} attended · {{completed}} completed', { attended: row.attended, count: row.count, completed: row.completed })}
                            </span>
                          ))}
                        </div>
                      )}
                      {!canMark && <p className="text-[11px] text-muted-foreground">{t('training:att_readonly', 'You can view attendance for this session but not mark it.')}</p>}
                      {canMark && isDraft && (
                        <p className="text-[11px] text-muted-foreground" data-testid="attendance-draft-note">
                          {t('training:att_draft', 'Attendance opens once this session is scheduled. Publish the program — that turns its draft sessions into scheduled ones and resolves the audience into participants.')}
                        </p>
                      )}
                      {canMark && session.status === 'cancelled' && (
                        <p className="text-[11px] text-muted-foreground" data-testid="attendance-cancelled-note">
                          {t('training:att_session_cancelled', 'This session was cancelled — attendance can no longer be marked.')}
                        </p>
                      )}
                      {attendanceQ.isLoading && <div className="text-xs text-muted-foreground">{t('training:loading', 'Loading')}…</div>}
                      {attendanceQ.isError && <p className="text-xs text-red-600 dark:text-red-400">{getApiErrorMessage(attendanceQ.error, t('training:err', 'Something went wrong'))}</p>}
                      {attendanceQ.data && (
                        <AttendanceGrid rows={attendanceQ.data} readOnly={!canMark || attendanceClosed} saving={mark.isPending}
                          onSave={(marks) => mark.mutate({ sessionId: session.id, marks }, { onSuccess: () => toast.success(t('training:att_saved', 'Attendance saved for {{n}} person(s)', { n: marks.length })) })} />
                      )}
                    </div>
                  )}

                  {tab === 'practice' && showPractice && (
                    <div className="space-y-2" role="tabpanel" data-testid="practice-tab">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('training:practice_queue', 'Practical work to validate')}</h3>
                      <p className="text-[11px] text-muted-foreground">{t('training:practice_queue_help', 'Submissions for this session. Who may validate follows the exercise\'s verification kind — the platform refuses the rest.')}</p>
                      <ValidationQueue sessionId={session.id} defaultStatus="submitted" />
                    </div>
                  )}

                  {tab === 'calendar' && showCalendar && (
                    <div className="space-y-2" role="tabpanel" data-testid="calendar-panel">
                      <div className="flex flex-wrap items-center gap-2">
                        <MirrorStatusBadge status={session.mirror_status} error={session.mirror_error} showPending />
                        {session.calendar_event_id && <span className="text-[11px] text-muted-foreground">{t('training:calendar_event', 'Event')} {String(session.calendar_event_id).slice(0, 8)}</span>}
                      </div>
                      {session.mirror_status === 'error' && session.mirror_error && (
                        <div role="alert" className="rounded-md border border-red-300 bg-red-50 dark:bg-red-500/10 p-2 text-xs text-red-700 dark:text-red-300">{session.mirror_error}</div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        {joinUrl
                          ? <a className="inline-flex items-center gap-1 text-orange-600 hover:underline" href={joinUrl} target="_blank" rel="noreferrer"><Link2 className="w-3.5 h-3.5" aria-hidden /> {joinUrl}</a>
                          : session.generate_meeting_link ? t('training:mint_link_pending', 'A meeting link will be minted when the session is published and mirrored.') : t('training:no_meeting_link', 'No meeting link.')}
                      </div>
                      <p className="text-[11px] text-muted-foreground">{t('training:calendar_help', 'Published sessions are mirrored to the company calendar for every participant. Re-sync after editing times or the roster if the mirror shows an error.')}</p>
                      {session.status !== 'cancelled' && (
                        <Button size="sm" variant="outline" disabled={sync.isPending} onClick={() => sync.mutate(session.id, {
                          onSuccess: (r) => (r.mirror_status === 'error' ? toast.error(r.mirror_error || t('training:mirror_error', 'Calendar sync failed')) : toast.success(t('training:calendar_synced', 'Calendar synced'))),
                        })}>
                          {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="mr-1 h-3.5 w-3.5" />{t('training:calendar_sync', 'Sync calendar')}</>}
                        </Button>
                      )}
                    </div>
                  )}
                </section>
              )}

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Calendar className="w-3.5 h-3.5" aria-hidden /> {t('training:session_id', 'Session')} {session.id.slice(0, 8)}
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SessionDetailSheet;
